"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard"
import {
  Clock,
  Globe,
  Calendar,
  Copy,
  RefreshCw,
  Plus,
  Minus,
  Sun,
  Moon,
  Hash,
  Timer
} from "lucide-react"

interface TimeZone {
  value: string
  label: string
  offset: string
  region: string
}

const TIME_ZONES: TimeZone[] = [
  // Americas
  { value: "America/New_York", label: "New York", offset: "-05:00", region: "Americas" },
  { value: "America/Chicago", label: "Chicago", offset: "-06:00", region: "Americas" },
  { value: "America/Denver", label: "Denver", offset: "-07:00", region: "Americas" },
  { value: "America/Los_Angeles", label: "Los Angeles", offset: "-08:00", region: "Americas" },
  { value: "America/Toronto", label: "Toronto", offset: "-05:00", region: "Americas" },
  { value: "America/Vancouver", label: "Vancouver", offset: "-08:00", region: "Americas" },
  { value: "America/Mexico_City", label: "Mexico City", offset: "-06:00", region: "Americas" },
  { value: "America/Sao_Paulo", label: "São Paulo", offset: "-03:00", region: "Americas" },
  { value: "America/Buenos_Aires", label: "Buenos Aires", offset: "-03:00", region: "Americas" },

  // Europe
  { value: "Europe/London", label: "London", offset: "+00:00", region: "Europe" },
  { value: "Europe/Paris", label: "Paris", offset: "+01:00", region: "Europe" },
  { value: "Europe/Berlin", label: "Berlin", offset: "+01:00", region: "Europe" },
  { value: "Europe/Madrid", label: "Madrid", offset: "+01:00", region: "Europe" },
  { value: "Europe/Rome", label: "Rome", offset: "+01:00", region: "Europe" },
  { value: "Europe/Moscow", label: "Moscow", offset: "+03:00", region: "Europe" },
  { value: "Europe/Kiev", label: "Kiev", offset: "+02:00", region: "Europe" },
  { value: "Europe/Istanbul", label: "Istanbul", offset: "+03:00", region: "Europe" },

  // Asia
  { value: "Asia/Dubai", label: "Dubai", offset: "+04:00", region: "Asia" },
  { value: "Asia/Kolkata", label: "India", offset: "+05:30", region: "Asia" },
  { value: "Asia/Shanghai", label: "Shanghai", offset: "+08:00", region: "Asia" },
  { value: "Asia/Hong_Kong", label: "Hong Kong", offset: "+08:00", region: "Asia" },
  { value: "Asia/Singapore", label: "Singapore", offset: "+08:00", region: "Asia" },
  { value: "Asia/Tokyo", label: "Tokyo", offset: "+09:00", region: "Asia" },
  { value: "Asia/Seoul", label: "Seoul", offset: "+09:00", region: "Asia" },

  // Oceania
  { value: "Australia/Sydney", label: "Sydney", offset: "+11:00", region: "Oceania" },
  { value: "Australia/Melbourne", label: "Melbourne", offset: "+11:00", region: "Oceania" },
  { value: "Australia/Perth", label: "Perth", offset: "+08:00", region: "Oceania" },
  { value: "Pacific/Auckland", label: "Auckland", offset: "+13:00", region: "Oceania" },

  // Others
  { value: "UTC", label: "UTC", offset: "+00:00", region: "Other" },
  { value: "Africa/Cairo", label: "Cairo", offset: "+02:00", region: "Africa" },
  { value: "Africa/Lagos", label: "Lagos", offset: "+01:00", region: "Africa" },
  { value: "Africa/Johannesburg", label: "Johannesburg", offset: "+02:00", region: "Africa" },
]

interface WorldClock {
  id: string
  timezone: string
  label: string
}

export function TimeConverter() {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [sourceTimezone, setSourceTimezone] = useState("America/New_York")
  const [targetTimezone, setTargetTimezone] = useState("Europe/London")
  const [inputTime, setInputTime] = useState("")
  const [inputDate, setInputDate] = useState("")
  const [convertedTime, setConvertedTime] = useState("")
  const [unixTimestamp, setUnixTimestamp] = useState("")
  const [unixToDate, setUnixToDate] = useState("")
  const [worldClocks, setWorldClocks] = useState<WorldClock[]>([
    { id: "1", timezone: "America/New_York", label: "New York" },
    { id: "2", timezone: "Europe/London", label: "London" },
    { id: "3", timezone: "Asia/Tokyo", label: "Tokyo" },
  ])

  // Update current time every second, pausing when tab is hidden
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null

    const start = () => {
      if (!timer) {
        setCurrentTime(new Date())
        timer = setInterval(() => setCurrentTime(new Date()), 1000)
      }
    }

    const stop = () => {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop()
      } else {
        start()
      }
    }

    start()
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      stop()
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [])

  useEffect(() => {
    let active = true
    queueMicrotask(() => {
      if (!active) return

      const now = new Date()
      const hours = String(now.getHours()).padStart(2, "0")
      const minutes = String(now.getMinutes()).padStart(2, "0")
      setInputTime(`${hours}:${minutes}`)

      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, "0")
      const day = String(now.getDate()).padStart(2, "0")
      setInputDate(`${year}-${month}-${day}`)
    })

    return () => {
      active = false
    }
  }, [])

  const formatTimeForTimezone = useCallback((date: Date, timezone: string) => {
    try {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }

      const formatter = new Intl.DateTimeFormat('en-US', options)
      return formatter.format(date)
    } catch {
      return "Invalid timezone"
    }
  }, [])

  const convertTime = useCallback(() => {
    if (!inputTime || !inputDate) {
      toast.error("Please enter both date and time")
      return
    }

    try {
      // Create a date object in the source timezone
      const dateTimeString = `${inputDate}T${inputTime}:00`
      const sourceDate = new Date(dateTimeString)

      // Get the converted time in target timezone
      const targetOptions: Intl.DateTimeFormatOptions = {
        timeZone: targetTimezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        weekday: 'long'
      }

      const targetFormatter = new Intl.DateTimeFormat('en-US', targetOptions)
      setConvertedTime(targetFormatter.format(sourceDate))

      toast.success(`Successfully converted from ${sourceTimezone} to ${targetTimezone}`)
    } catch {
      toast.error("Conversion failed: Invalid date/time format")
    }
  }, [inputTime, inputDate, sourceTimezone, targetTimezone])

  const convertUnixToDate = useCallback(() => {
    if (!unixTimestamp) {
      toast.error("Please enter a Unix timestamp")
      return
    }

    try {
      const timestamp = parseInt(unixTimestamp)
      let date: Date

      // Check if it's in seconds or milliseconds
      if (timestamp < 10000000000) {
        // Likely in seconds
        date = new Date(timestamp * 1000)
      } else {
        // Likely in milliseconds
        date = new Date(timestamp)
      }

      if (isNaN(date.getTime())) {
        throw new Error("Invalid timestamp")
      }

      const formatted = date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      })

      setUnixToDate(formatted)
      toast.success("Unix timestamp converted to date")
    } catch {
      toast.error("Conversion failed: Invalid Unix timestamp")
    }
  }, [unixTimestamp])

  const convertDateToUnix = useCallback(() => {
    if (!inputDate || !inputTime) {
      toast.error("Please enter both date and time")
      return
    }

    try {
      const dateTimeString = `${inputDate}T${inputTime}:00`
      const date = new Date(dateTimeString)

      if (isNaN(date.getTime())) {
        throw new Error("Invalid date")
      }

      const timestampSeconds = Math.floor(date.getTime() / 1000)
      const timestampMillis = date.getTime()

      setUnixTimestamp(timestampSeconds.toString())
      toast.success(`Converted! Seconds: ${timestampSeconds}, Milliseconds: ${timestampMillis}`)
    } catch {
      toast.error("Conversion failed: Invalid date/time format")
    }
  }, [inputDate, inputTime])

  const addWorldClock = useCallback(() => {
    const newClock: WorldClock = {
      id: Date.now().toString(),
      timezone: "UTC",
      label: "UTC"
    }
    setWorldClocks([...worldClocks, newClock])
  }, [worldClocks])

  const removeWorldClock = useCallback((id: string) => {
    setWorldClocks(worldClocks.filter(clock => clock.id !== id))
  }, [worldClocks])

  const updateWorldClock = useCallback((id: string, timezone: string) => {
    const zone = TIME_ZONES.find(tz => tz.value === timezone)
    if (zone) {
      setWorldClocks(worldClocks.map(clock =>
        clock.id === id
          ? { ...clock, timezone, label: zone.label }
          : clock
      ))
    }
  }, [worldClocks])

  const { copy: handleCopy } = useCopyToClipboard()

  const getTimeOfDay = (date: Date, timezone: string) => {
    const hour = parseInt(formatTimeForTimezone(date, timezone).split(' ')[1].split(':')[0])
    if (hour >= 5 && hour < 12) return "morning"
    if (hour >= 12 && hour < 17) return "afternoon"
    if (hour >= 17 && hour < 21) return "evening"
    return "night"
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Time Zone Converter</CardTitle>
          <CardDescription>
            Convert time between different time zones and formats
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="timezone" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="timezone">
                <Globe className="h-4 w-4 mr-2" />
                Time Zones
              </TabsTrigger>
              <TabsTrigger value="unix">
                <Hash className="h-4 w-4 mr-2" />
                Unix Timestamp
              </TabsTrigger>
              <TabsTrigger value="world">
                <Clock className="h-4 w-4 mr-2" />
                World Clock
              </TabsTrigger>
            </TabsList>

            <TabsContent value="timezone" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="input-date">Date</Label>
                  <Input
                    id="input-date"
                    type="date"
                    value={inputDate}
                    onChange={(e) => setInputDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="input-time">Time</Label>
                  <Input
                    id="input-time"
                    type="time"
                    value={inputTime}
                    onChange={(e) => setInputTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source-tz">From Time Zone</Label>
                  <Select value={sourceTimezone} onValueChange={setSourceTimezone}>
                    <SelectTrigger id="source-tz">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_ZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          <div className="flex items-center justify-between w-full">
                            <span>{tz.label}</span>
                            <Badge variant="outline" className="ml-2">
                              {tz.offset}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target-tz">To Time Zone</Label>
                  <Select value={targetTimezone} onValueChange={setTargetTimezone}>
                    <SelectTrigger id="target-tz">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_ZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          <div className="flex items-center justify-between w-full">
                            <span>{tz.label}</span>
                            <Badge variant="outline" className="ml-2">
                              {tz.offset}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={convertTime} className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Convert Time
              </Button>

              {convertedTime && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Converted Time:</p>
                      <p className="text-lg font-medium">{convertedTime}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopy(convertedTime)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="unix" className="space-y-4">
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Current Unix Timestamp:</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-mono font-bold">
                      {Math.floor(currentTime.getTime() / 1000)}
                    </p>
                    <Badge variant="secondary">seconds</Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <p className="text-lg font-mono">
                      {currentTime.getTime()}
                    </p>
                    <Badge variant="secondary">milliseconds</Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unix-input">Unix Timestamp to Date</Label>
                  <div className="flex gap-2">
                    <Input
                      id="unix-input"
                      placeholder="Enter Unix timestamp"
                      value={unixTimestamp}
                      onChange={(e) => setUnixTimestamp(e.target.value)}
                      className="font-mono"
                    />
                    <Button onClick={convertUnixToDate}>
                      Convert
                    </Button>
                  </div>
                  {unixToDate && (
                    <div className="p-3 bg-secondary rounded-md">
                      <p className="text-sm">{unixToDate}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Date to Unix Timestamp</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={inputDate}
                      onChange={(e) => setInputDate(e.target.value)}
                    />
                    <Input
                      type="time"
                      value={inputTime}
                      onChange={(e) => setInputTime(e.target.value)}
                    />
                  </div>
                  <Button onClick={convertDateToUnix} className="w-full">
                    Convert to Unix
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="world" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">World Clocks</h3>
                <Button variant="outline" size="sm" onClick={addWorldClock}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Clock
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {worldClocks.map((clock) => {
                  const time = formatTimeForTimezone(currentTime, clock.timezone)
                  const timeOfDay = getTimeOfDay(currentTime, clock.timezone)

                  return (
                    <Card key={clock.id} className="relative">
                      <CardContent className="pt-6">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2"
                          onClick={() => removeWorldClock(clock.id)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>

                        <div className="space-y-2">
                          <Select
                            value={clock.timezone}
                            onValueChange={(tz) => updateWorldClock(clock.id, tz)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_ZONES.map((tz) => (
                                <SelectItem key={tz.value} value={tz.value}>
                                  {tz.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <div className="text-center py-2">
                            <div className="flex items-center justify-center mb-2">
                              {timeOfDay === "morning" || timeOfDay === "afternoon" ? (
                                <Sun className="h-5 w-5 text-yellow-500 mr-2" />
                              ) : (
                                <Moon className="h-5 w-5 text-blue-500 mr-2" />
                              )}
                              <Badge variant="outline">{timeOfDay}</Badge>
                            </div>
                            <p className="text-2xl font-bold font-mono">
                              {time.split(' ')[1]}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {time.split(' ')[0]}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Timer className="h-4 w-4" />
                  <p className="text-sm font-medium">Meeting Time Optimizer</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Best time for all zones: Check when most locations are in business hours (9 AM - 5 PM)
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Quick Reference
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Common Formats</h4>
              <div className="space-y-1 font-mono text-xs">
                <div>ISO 8601: {new Date().toISOString()}</div>
                <div>RFC 2822: {new Date().toUTCString()}</div>
                <div>Locale: {new Date().toLocaleString()}</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Tips</h4>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• Unix timestamps are in UTC</li>
                <li>• Daylight Saving Time is handled automatically</li>
                <li>• Business hours: typically 9 AM - 5 PM local time</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}